import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { appendSourceParameter, sendEvent } from "@/gtm/functions";
import { IGTMEventType } from "@/gtm/IGTMEventType";
import Button from "@/app/components/Button/button";
import { useUpsellSubscriptionContext } from "./upsell-subscription-context";
import Skeleton from "react-loading-skeleton";
import SubscriptionPackages from "../botb-pass/subscriptionPackages";
import { getPassData } from "../botb-pass/subscription-service";
import Logo from "@/app/(general-header)/header/logo";

export const UpsellSubscriptionModalContent = ({
  isUpsellModal,
  updateMilestones,
}: {
  isUpsellModal?: boolean;
  updateMilestones?: Dispatch<SetStateAction<string>>;
}) => {
  const { onFirstTimeMayBeLater } = useUpsellSubscriptionContext();

  const [activeTabIndex, setActiveTabIndex] = useState<number>(1);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const [data, setData] = useState<BotBPassProps>();

  useEffect(() => {
    sendEvent(
      isUpsellModal ? IGTMEventType.SubscriptionUpsellPopupView : IGTMEventType.SubscriptionPageView,
      appendSourceParameter(isUpsellModal)
    );
    (async () => {
      try {
        const response = await getPassData();
        const data = (await response.json()) as BotBPassProps;
        setData(data);
        updateMilestones(data.Milestones);
      } catch (err) {
        console.error(`Error while retreiving subscriptions - ${JSON.stringify(err)}`);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  const getPostalEntryTextContent = () =>
    isLoading ? (
      <Skeleton
        count={1}
        inline
        highlightColor="#fff"
        containerClassName="flex flex-col w-full px-12 pb-5 h-[500px]"
        className="h-full"
      />
    ) : (
      <div className="bg-[#E7E7E9] lg:pb-20">
        <div
          dangerouslySetInnerHTML={{
            __html: data?.PostalEntryText,
          }}
          className="rounded-lg m-auto px-4 py-6 lg:bg-white lg:mt-8 lg:p-8 max-w-[664px] [&>h2]:normal-case [&>h2]:text-secondary [&>h2]:text-left [&>h2]:text-2xl [&>h2]:leading-tight [&>h2]:mb-3 [&>h2]:font-medium [&>p]:mb-6 [&>p]:text-gray-1 [&>p]:text-sm [&>p]:tracking-normal [&>p:last-of-type]:mb-0 [&>ul]:mb-6 [&>ul>li]:text-gray-1 [&>ul>li]:text-sm [&>ul>li]:tracking-normal"
        />
      </div>
    );

  return (
    <div className="flex flex-col">
      <div className="flex items-center h-[57px] relative">
        <Logo />
        <Button
          className="text-[#696971] font-jost text-[14px] font-medium underline absolute right-3"
          onClick={onFirstTimeMayBeLater}
          text="Maybe later"
        />
      </div>
      <hr />
      <div className="flex flex-col items-center pt-5">
        <h5 className="text-[#FF8200] text-center mb-4 font-jost xs:text-[28px] lg:text-[32px] lg:uppercase xs:font-medium lg:font-normal xs:w-[310px] lg:w-full">
          Don't miss a draw again!
        </h5>

        <div className="w-full bg-[#E7E7E9]">
          <div className="flex flex-col items-center bg-white relative">
            <div id={"main-plans-section"} className="w-full flex flex-col gap-4 lg:gap-6 items-center relative">
              <div className={"text-center px-3 mt-4 lg:mt-8"}>
                <div
                  className={`w-[336px] text-primary text-center font-jost text-xl font-medium leading-[26px] lg:text-[28px] lg:font-semibold lg:leading-8`}>
                  Choose how to play:
                </div>
              </div>
              <div className="flex xs:w-full lg:w-auto">
                <div
                  className={`w-1/2 lg:w-[210px] px-2 cursor-pointer items-center justify-center flex flex-col border-b-1.5 lg:border-b-2 border-solid ${activeTabIndex === 0 ? "border-[#FF8200]" : "border-transparent"}`}
                  onClick={() => setActiveTabIndex(0)}>
                  <div className={"flex flex-col items-center pb-2.5 lg:pb-4"}>
                    <span
                      className={`uppercase font-jost text-sm leading-5 font-medium lg:text-base lg:leading-[1.375rem] ${activeTabIndex === 0 ? "text-[#28282B]" : "text-[#696971]"}`}>
                      Postal
                    </span>
                    <span className="text-[#898994] font-jost text-[11px] lg:text-sm font-normal -mt-0.5">
                      No purchase necessary
                    </span>
                  </div>
                </div>
                <div
                  className={`w-1/2 lg:w-[210px] px-2 cursor-pointer items-center justify-center flex flex-col border-b-1.5 lg:border-b-2 border-solid ${activeTabIndex === 1 ? "border-[#FF8200]" : "border-transparent"}`}
                  onClick={() => setActiveTabIndex(1)}>
                  <div className={"flex flex-col items-center pb-2.5 lg:pb-4"}>
                    <span
                      className={`uppercase font-jost text-sm leading-5 font-medium lg:text-base lg:leading-[1.375rem] ${activeTabIndex === 1 ? "text-[#28282B]" : "text-[#696971]"}`}>
                      Subscription
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {activeTabIndex === 0 ? (
            getPostalEntryTextContent()
          ) : (
            <div className={"my-6 lg:mb-20 lg:mt-8"}>
              <SubscriptionPackages isUpsellModal={true} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
